import nop from "util/nop";

export function stepActionOf(url: string): string | null {
  return new URL(url, window.location.href).searchParams.get("step_action");
}

// The browser agent only names interactions for page loads and URL changes, so
// an in-page action's request would otherwise carry no browserInteractionName
// and the Form Interaction SLOs could not tell price updates from instant
// actions. Returns the function that ends the interaction; until it runs, the
// interaction and its requests are not sent to New Relic.
export function startNamedInteraction(name: string): () => void {
  // While the page is loading, requests that start while this interaction is
  // open, such as analytics calls, would be attributed to it rather than to the
  // page load, and carry the action's name, so we only create a new
  // interaction once the page is ready. This would be rare.
  if (!window.newrelic || document.readyState !== "complete") {
    return nop;
  }

  try {
    const interaction = window.newrelic.interaction({ waitForEnd: true });
    // Without save(), the agent discards an interaction that changes no URL.
    interaction.setName(name).save();
    return () => {
      try {
        interaction.end();
      } catch {
        // Monitoring must never break the customer's action.
      }
    };
  } catch {
    return nop;
  }
}

export async function withNamedInteraction<T>(
  name: string | null,
  request: () => Promise<T>,
): Promise<T> {
  const end = name ? startNamedInteraction(name) : nop;
  try {
    return await request();
  } finally {
    end();
  }
}
